import { useTranslation } from 'react-i18next';

import {
  Address,
  DraftEmailType,
  EmailProvider,
  EmailRequestType,
  UnifiedAttachment,
  UnifiedSendEmailRequest,
} from '@this+that/models';
import log from 'loglevel';

import { DraftConflictResult } from './draftConflict';
import { DraftAccount, resolveDraftAccount } from './resolveEmailAccount';
import { isDraftNotFoundError } from './useDeleteDraft';
import {
  DraftVersionConflictError,
  sendEmails,
} from '../../../services/unifiedEmailAction.service';
import { useIntegrationsStore } from '../../../store';
import {
  extractUserInputFromTemplate,
  hasTemplateContent,
} from '../../../utils/editorContentUtils';
import { getAddressesByType } from '../../../utils/getAddressesByType';
import { useCustomToast } from '../../useCustomToast';

const hasAddressMissingType = (addresses: Address[]): boolean =>
  addresses.some((address) => !address.type);

export interface SendDraftProperties {
  draftId: string;
  toAddresses: Address[];
  ccAddresses?: Address[];
  bccAddresses?: Address[];
  body: string;
  subject: string;
  attachments?: UnifiedAttachment[];
  fromEmail?: string;
  draftType?: DraftEmailType;
  modifiedQuotedContent?: string;
  expectedDraftVersion?: string;
  pinnedAccount?: DraftAccount;
}

export const useSendDraft = () => {
  const { t } = useTranslation();
  const { currentIntegrationAccount, integrationAccounts } =
    useIntegrationsStore();
  const { errorMessage } = useCustomToast();

  const sendDraft = async (draftProperties: SendDraftProperties) => {
    try {
      if (!currentIntegrationAccount) {
        log.error('No integration mail found');
        errorMessage(t('inbox.userCommunication.actions.sendDraftError'));
        return null;
      }

      if (!draftProperties.draftId) {
        log.error('Draft ID is required to send draft');
        errorMessage(t('inbox.userCommunication.actions.sendDraftError'));
        return null;
      }

      if (
        !draftProperties.toAddresses ||
        draftProperties.toAddresses.length === 0
      ) {
        log.error('At least one recipient is required to send draft');
        errorMessage(t('inbox.userCommunication.actions.sendDraftError'));
        return null;
      }

      if (
        hasAddressMissingType(draftProperties.toAddresses) ||
        hasAddressMissingType(draftProperties.ccAddresses ?? []) ||
        hasAddressMissingType(draftProperties.bccAddresses ?? [])
      ) {
        log.error(
          'Recipient address missing RecipientType, would be dropped by getAddressesByType',
        );
        errorMessage(t('inbox.userCommunication.actions.sendDraftError'));
        return null;
      }

      if (!draftProperties.subject || !draftProperties.body) {
        log.error('Subject and body are required to send draft');
        errorMessage(t('inbox.userCommunication.actions.sendDraftError'));
        return null;
      }

      const selectedAccount = resolveDraftAccount(
        draftProperties.fromEmail,
        currentIntegrationAccount,
        integrationAccounts,
        { strict: true },
      );

      if (!selectedAccount) {
        errorMessage(t('inbox.userCommunication.actions.sendDraftError'));
        return null;
      }

      const resolved = draftProperties.pinnedAccount ?? selectedAccount;

      const { accountId: fromEmailAccountId, platform } = resolved;

      let bodyContent = draftProperties.body;
      if (
        draftProperties.body &&
        hasTemplateContent(draftProperties.body) &&
        (draftProperties.draftType === DraftEmailType.REPLY ||
          draftProperties.draftType === DraftEmailType.FORWARD)
      ) {
        bodyContent = extractUserInputFromTemplate(draftProperties.body);
      }

      const allRecipients = [
        ...draftProperties.toAddresses,
        ...(draftProperties.ccAddresses || []),
        ...(draftProperties.bccAddresses || []),
      ];

      const {
        to: toAddresses,
        cc: ccAddresses,
        bcc: bccAddresses,
      } = getAddressesByType(allRecipients);

      const payload: UnifiedSendEmailRequest = {
        providerAccount: {
          provider: platform as EmailProvider,
          accountId: fromEmailAccountId,
        },
        reqType: EmailRequestType.SEND_DRAFT,
        draftId: draftProperties.draftId,
        // The compositor owns the full reviewed recipient lists — an empty Cc/Bcc must
        // reach the provider as "clear this field", not be collapsed to undefined ("keep
        // whatever the draft already has").
        toAddresses,
        ccAddresses,
        bccAddresses,
        body: bodyContent,
        subject: draftProperties.subject,
        attachments: draftProperties.attachments,
        draftType: draftProperties.draftType,
        modifiedQuotedContent: draftProperties.modifiedQuotedContent,
        expectedDraftVersion: draftProperties.expectedDraftVersion,
      };

      const response = await sendEmails(payload);

      return response;
    } catch (error) {
      if (error instanceof DraftVersionConflictError) {
        log.warn('Draft version conflict while sending:', error);
        errorMessage(
          t('inbox.userCommunication.actions.sendDraftConflictError'),
        );
        const conflictResult: DraftConflictResult = {
          isDraftConflict: true,
          currentDraftVersion: error.currentDraftVersion,
        };
        return conflictResult;
      }
      log.error('Failed to send draft:', error);
      errorMessage(
        t(
          isDraftNotFoundError(error)
            ? 'inbox.userCommunication.actions.draftNotFoundInAccountError'
            : 'inbox.userCommunication.actions.sendDraftError',
        ),
      );
      return null;
    }
  };

  return {
    sendDraft,
  };
};
